import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2, Circle, ArrowRight, Sparkles,
  Plug, Target, Lightbulb, Zap, ListChecks, Award, Search, Beaker
} from 'lucide-react';
import { apiCallJson } from '../utils/api';
import { useTheme } from '../context/ThemeContext';

// Lucide icon per milestone id. Keep in sync with onboarding-service.js.
const ICONS = {
  connect_account: Plug,
  set_targets: Target,
  fill_offer_analyzer: Lightbulb,
  run_analysis: Zap,
  apply_recommendation: ListChecks,
  run_quality_score: Award,
  try_keyword_research: Search,
  start_ab_test: Beaker
};

// Milestones whose source-of-truth is real DB state. Toggling the checkbox
// down for these has no effect (the detector keeps reporting complete), so
// we render them as non-toggleable to avoid confusing the user.
const AUTO_DETECTED_IDS = new Set([
  'connect_account',
  'set_targets',
  'fill_offer_analyzer',
  'apply_recommendation',
  'start_ab_test'
]);

/**
 * Full-page onboarding checklist. Rendered as the "Get started" tab.
 * Pass onNavigate(view) so milestone CTAs can jump to the relevant tool.
 */
export default function OnboardingChecklist({ onNavigate }) {
  const { theme } = useTheme();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    try {
      const data = await apiCallJson('/api/onboarding/status');
      if (data?.success) {
        setStatus(data);
      } else {
        setError(data?.error || 'Failed to load checklist');
      }
    } catch (err) {
      setError(err.message || 'Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleStart = (milestone) => {
    if (!milestone?.targetView) return;
    if (typeof onNavigate === 'function') {
      onNavigate(milestone.targetView);
    }
  };

  // Toggle the manual completion flag for a milestone. The server resolves
  // final completion as (manual flag OR DB detector), so unchecking an
  // auto-detected milestone has no effect — we gate that on the client too.
  const handleToggle = async (milestone) => {
    if (!milestone) return;
    if (milestone.completed && AUTO_DETECTED_IDS.has(milestone.id)) {
      // No-op: real DB state says this is done.
      return;
    }
    const nextCompleted = !milestone.completed;
    // Optimistic UI: flip locally first so the click feels instant.
    setStatus(prev => {
      if (!prev) return prev;
      const milestones = prev.milestones.map(m =>
        m.id === milestone.id ? { ...m, completed: nextCompleted } : m
      );
      const completedCount = milestones.filter(m => m.completed).length;
      const total = milestones.length;
      return {
        ...prev,
        milestones,
        completedCount,
        progressPct: total === 0 ? 100 : Math.round((completedCount / total) * 100)
      };
    });
    try {
      await apiCallJson('/api/onboarding/track-event', {
        method: 'POST',
        body: { milestoneId: milestone.id, completed: nextCompleted }
      });
      // Refetch to reconcile with the server's source-of-truth (in case a DB
      // detector reports something different than the optimistic state).
      fetchStatus();
    } catch (err) {
      // On failure, refetch so the UI snaps back to truth.
      fetchStatus();
    }
  };

  const styles = useMemo(() => ({
    page: {
      padding: 40,
      maxWidth: 980,
      margin: '0 auto',
      color: theme.colors.text.primary
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      marginBottom: 8
    },
    iconBadge: {
      width: 56,
      height: 56,
      borderRadius: 16,
      background: theme.colors.brand.gradient,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 8px 24px rgba(139, 92, 246, 0.25)'
    },
    title: {
      fontSize: 32,
      fontWeight: 700,
      margin: 0,
      color: theme.colors.text.primary
    },
    subtitle: {
      color: theme.colors.text.secondary,
      fontSize: 16,
      marginTop: 6,
      marginBottom: 32,
      lineHeight: 1.55
    },
    progressCard: {
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 20,
      padding: 24,
      marginBottom: 24
    },
    progressRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 14
    },
    progressLabel: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.colors.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    progressNumber: {
      fontSize: 28,
      fontWeight: 700,
      color: theme.colors.text.primary
    },
    progressBarOuter: {
      width: '100%',
      height: 10,
      borderRadius: 999,
      background: theme.colors.bg.tertiary,
      overflow: 'hidden'
    },
    progressBarInner: (pct) => ({
      height: '100%',
      width: `${pct}%`,
      background: theme.colors.brand.gradient,
      transition: 'width 0.4s ease'
    }),
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12
    },
    item: (completed) => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: 16,
      padding: 20,
      background: theme.colors.bg.secondary,
      border: `1px solid ${completed ? 'rgba(16, 185, 129, 0.35)' : theme.colors.border.primary}`,
      borderRadius: 16,
      transition: 'all 0.2s ease',
      opacity: completed ? 0.85 : 1
    }),
    checkbox: (completed, locked) => ({
      width: 28,
      height: 28,
      borderRadius: '50%',
      flexShrink: 0,
      marginTop: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: completed ? theme.colors.status.success : 'transparent',
      border: completed
        ? `2px solid ${theme.colors.status.success}`
        : `2px solid ${theme.colors.border.primary}`,
      color: completed ? '#ffffff' : 'transparent',
      cursor: locked ? 'default' : 'pointer',
      transition: 'all 0.15s ease',
      padding: 0
    }),
    itemIconWrap: (completed) => ({
      width: 36,
      height: 36,
      borderRadius: 10,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: completed
        ? 'rgba(16, 185, 129, 0.12)'
        : theme.colors.bg.tertiary,
      color: completed
        ? theme.colors.status.success
        : theme.colors.brand.primary
    }),
    itemBody: {
      flex: 1,
      minWidth: 0
    },
    itemLabel: (completed) => ({
      fontSize: 16,
      fontWeight: 600,
      color: theme.colors.text.primary,
      textDecoration: completed ? 'line-through' : 'none',
      marginBottom: 4
    }),
    itemDescription: {
      fontSize: 14,
      color: theme.colors.text.secondary,
      lineHeight: 1.5
    },
    ctaButton: (completed) => ({
      flexShrink: 0,
      padding: '10px 16px',
      borderRadius: 12,
      border: completed
        ? `1px solid ${theme.colors.border.primary}`
        : 'none',
      background: completed
        ? 'transparent'
        : theme.colors.brand.gradient,
      color: completed
        ? theme.colors.text.tertiary
        : '#ffffff',
      fontSize: 14,
      fontWeight: 600,
      cursor: completed ? 'default' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      whiteSpace: 'nowrap'
    }),
    completionBanner: {
      marginTop: 24,
      padding: 24,
      borderRadius: 16,
      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.18) 0%, rgba(16, 185, 129, 0.08) 100%)',
      border: '1px solid rgba(16, 185, 129, 0.4)',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      color: theme.colors.text.primary
    },
    error: {
      padding: 20,
      borderRadius: 12,
      background: 'rgba(239, 68, 68, 0.1)',
      border: `1px solid ${theme.colors.status.error}`,
      color: theme.colors.status.error
    },
    skeleton: {
      padding: 20,
      borderRadius: 16,
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      color: theme.colors.text.secondary
    }
  }), [theme]);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.skeleton}>Loading your checklist…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  if (!status) return null;

  const { milestones, completedCount, total, progressPct, gracePeriodOver } = status;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.iconBadge}>
          <Sparkles size={28} color="#ffffff" />
        </div>
        <div>
          <h1 style={styles.title}>Get started with Bidoryx</h1>
        </div>
      </div>
      <p style={styles.subtitle}>
        Knock out these {total} steps to set yourself up for the wins. Most users
        finish them in under 30 minutes — and the impact compounds from there.
      </p>

      <div style={styles.progressCard}>
        <div style={styles.progressRow}>
          <span style={styles.progressLabel}>Your progress</span>
          <span style={styles.progressNumber}>{completedCount}/{total}</span>
        </div>
        <div style={styles.progressBarOuter}>
          <div style={styles.progressBarInner(progressPct)} />
        </div>
      </div>

      <div style={styles.list}>
        {milestones.map((m) => {
          const Icon = ICONS[m.id] || Circle;
          // Auto-detected milestones lock the checkbox once complete — the
          // DB state is the truth and unticking would just bounce back.
          const checkboxLocked = m.completed && AUTO_DETECTED_IDS.has(m.id);
          return (
            <div key={m.id} style={styles.item(m.completed)}>
              <button
                type="button"
                style={styles.checkbox(m.completed, checkboxLocked)}
                onClick={() => handleToggle(m)}
                disabled={checkboxLocked}
                aria-label={m.completed ? `Mark "${m.label}" as not done` : `Mark "${m.label}" as done`}
                title={
                  checkboxLocked
                    ? 'Auto-completed from your account activity'
                    : m.completed
                      ? 'Click to mark as not done'
                      : 'Click to mark as done'
                }
              >
                {m.completed && <CheckCircle2 size={20} strokeWidth={2.5} />}
              </button>
              <div style={styles.itemIconWrap(m.completed)}>
                <Icon size={20} />
              </div>
              <div style={styles.itemBody}>
                <div style={styles.itemLabel(m.completed)}>{m.label}</div>
                <div style={styles.itemDescription}>{m.description}</div>
              </div>
              <button
                style={styles.ctaButton(m.completed)}
                onClick={() => !m.completed && handleStart(m)}
                disabled={m.completed}
              >
                {m.completed ? 'Done' : (m.cta || 'Start')}
                {!m.completed && <ArrowRight size={16} />}
              </button>
            </div>
          );
        })}
      </div>

      {progressPct === 100 && (
        <div style={styles.completionBanner}>
          <CheckCircle2 size={28} color={theme.colors.status.success} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
              You're all set!
            </div>
            <div style={{ color: theme.colors.text.secondary, fontSize: 14 }}>
              You've completed every step — Bidoryx is now working at full strength
              for you. This tab will hide itself shortly.
            </div>
          </div>
        </div>
      )}

      {gracePeriodOver && progressPct < 100 && (
        <div style={{ ...styles.completionBanner, background: 'transparent', border: `1px solid ${theme.colors.border.primary}` }}>
          <Sparkles size={24} color={theme.colors.brand.primary} />
          <div style={{ color: theme.colors.text.secondary, fontSize: 14 }}>
            You can finish these any time. We'll keep this tab around in Help &
            Support.
          </div>
        </div>
      )}
    </div>
  );
}
